import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from '@react-pdf/renderer';

// Company details
const COMPANY = {
  name: 'Polatkan Turizm',
  address: 'Gazeteci Kemal Togan Sk',
  city: 'Lefkosa 99010',
  phone: '(0392) 444 02 22',
  email: 'info@polatkanturizm.com',
  bank: 'Is Bankasi',
  accountName: 'Polatkan Turizm Ltd',
  iban: 'TR00 0000 0000 0000 0000 0000 00',
};

// Color scheme
const colors = {
  primary: '#1e40af',
  secondary: '#3b82f6',
  text: '#1f2937',
  textLight: '#6b7280',
  border: '#e5e7eb',
  bgLight: '#f9fafb',
  success: '#059669',
  warning: '#d97706',
  danger: '#dc2626',
};

// Status styles (separate from StyleSheet for dynamic use)
const statusStyles = {
  paid: {
    backgroundColor: '#d1fae5',
    color: '#059669',
  },
  draft: {
    backgroundColor: '#f3f4f6',
    color: '#6b7280',
  },
  sent: {
    backgroundColor: '#dbeafe',
    color: '#1e40af',
  },
  overdue: {
    backgroundColor: '#fee2e2',
    color: '#dc2626',
  },
  partiallyPaid: {
    backgroundColor: '#fef3c7',
    color: '#d97706',
  },
};

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 10,
    fontFamily: 'Helvetica',
    color: colors.text,
    backgroundColor: '#ffffff',
  },
  // Header styles
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 25,
    paddingBottom: 20,
    borderBottomWidth: 2,
    borderBottomColor: colors.primary,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  logo: {
    width: 60,
    height: 60,
    marginRight: 12,
  },
  companyDetails: {
    justifyContent: 'center',
  },
  companyName: {
    fontSize: 20,
    fontWeight: 'bold',
    color: colors.primary,
    marginBottom: 4,
  },
  companyAddress: {
    fontSize: 9,
    color: colors.textLight,
    marginBottom: 2,
  },
  headerRight: {
    textAlign: 'right',
    alignItems: 'flex-end',
  },
  invoiceTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: colors.primary,
    marginBottom: 8,
  },
  invoiceNumber: {
    fontSize: 11,
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: 4,
  },
  invoiceDate: {
    fontSize: 9,
    color: colors.textLight,
    marginBottom: 2,
  },
  statusBadge: {
    paddingVertical: 4,
    paddingHorizontal: 12,
    borderRadius: 4,
    marginTop: 8,
  },
  statusText: {
    fontSize: 9,
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  // Info boxes
  infoBoxesContainer: {
    flexDirection: 'row',
    marginBottom: 25,
    gap: 15,
  },
  infoBox: {
    flex: 1,
    backgroundColor: colors.bgLight,
    padding: 15,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: colors.border,
  },
  infoBoxTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    color: colors.primary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 10,
  },
  infoBoxName: {
    fontSize: 11,
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: 4,
  },
  infoBoxText: {
    fontSize: 9,
    color: colors.textLight,
    marginBottom: 2,
  },
  infoBoxLabel: {
    fontSize: 8,
    color: colors.textLight,
    textTransform: 'uppercase',
    marginTop: 6,
    marginBottom: 2,
  },
  infoBoxValue: {
    fontSize: 9,
    color: colors.text,
    marginBottom: 2,
  },
  // Table styles
  table: {
    marginBottom: 20,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 4,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: colors.primary,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  tableHeaderCell: {
    color: '#ffffff',
    fontSize: 9,
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  tableRowAlt: {
    flexDirection: 'row',
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.bgLight,
  },
  tableRowNoBorder: {
    flexDirection: 'row',
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  tableRowAltNoBorder: {
    flexDirection: 'row',
    paddingVertical: 10,
    paddingHorizontal: 12,
    backgroundColor: colors.bgLight,
  },
  tableCell: {
    fontSize: 9,
    color: colors.text,
  },
  tableCellDescription: {
    flex: 4,
  },
  tableCellQty: {
    flex: 1,
    textAlign: 'center',
  },
  tableCellPrice: {
    flex: 1.5,
    textAlign: 'right',
  },
  tableCellTax: {
    flex: 1,
    textAlign: 'right',
  },
  tableCellTotal: {
    flex: 1.5,
    textAlign: 'right',
  },
  // Totals section
  totalsContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginBottom: 20,
  },
  totalsBox: {
    width: 240,
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  totalLabel: {
    fontSize: 9,
    color: colors.textLight,
  },
  totalValue: {
    fontSize: 9,
    fontWeight: 'bold',
    color: colors.text,
  },
  discountValue: {
    fontSize: 9,
    fontWeight: 'bold',
    color: colors.success,
  },
  grandTotalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 10,
    paddingHorizontal: 10,
    backgroundColor: colors.primary,
    borderRadius: 4,
    marginTop: 4,
  },
  grandTotalLabel: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  grandTotalValue: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  balanceDueRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 10,
    paddingHorizontal: 10,
    backgroundColor: '#fef3c7',
    borderWidth: 1,
    borderColor: '#f59e0b',
    borderRadius: 4,
    marginTop: 8,
  },
  balanceDueLabel: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#92400e',
  },
  balanceDueValue: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#92400e',
  },
  paidInFullRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 10,
    paddingHorizontal: 10,
    backgroundColor: '#d1fae5',
    borderWidth: 1,
    borderColor: colors.success,
    borderRadius: 4,
    marginTop: 8,
  },
  paidInFullLabel: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#065f46',
  },
  paidInFullValue: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#065f46',
  },
  // Payment details
  paymentDetailsBox: {
    backgroundColor: colors.bgLight,
    padding: 15,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: colors.border,
    marginBottom: 20,
  },
  paymentDetailsTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    color: colors.primary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 10,
  },
  paymentDetailsRow: {
    flexDirection: 'row',
    marginBottom: 4,
  },
  paymentDetailsLabel: {
    width: 100,
    fontSize: 9,
    color: colors.textLight,
  },
  paymentDetailsValue: {
    flex: 1,
    fontSize: 9,
    color: colors.text,
    fontWeight: 'bold',
  },
  // Notes and Terms
  notesBox: {
    backgroundColor: colors.bgLight,
    padding: 15,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: colors.border,
    marginBottom: 15,
  },
  notesTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    color: colors.primary,
    marginBottom: 8,
  },
  notesText: {
    fontSize: 9,
    color: colors.text,
    lineHeight: 1.5,
  },
  // Footer
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 40,
    right: 40,
    textAlign: 'center',
    borderTopWidth: 1,
    borderTopColor: colors.border,
    paddingTop: 15,
  },
  footerThankYou: {
    fontSize: 10,
    fontWeight: 'bold',
    color: colors.primary,
    marginBottom: 6,
  },
  footerContact: {
    fontSize: 8,
    color: colors.textLight,
  },
});

interface InvoiceItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  taxRate: number;
  taxAmount: number;
  totalAmount: number;
}

interface InvoicePDFProps {
  invoice: {
    invoiceNumber: string;
    invoiceType: string;
    invoiceTitle?: string | null;
    status: string;
    issueDate: string;
    dueDate: string;
    paidAt: string | null;
    subtotal: number;
    discountAmount: number;
    taxAmount: number;
    totalAmount: number;
    amountPaid: number;
    balanceDue: number;
    currency: string;
    notes: string | null;
    terms: string | null;
    // Bill-to override fields
    billToName?: string | null;
    billToCompany?: string | null;
    billToTaxId?: string | null;
    billToAddress?: string | null;
    billToEmail?: string | null;
    billToPhone?: string | null;
    customer: {
      firstName: string;
      lastName: string;
      email: string;
      phone: string | null;
      address?: string | null;
      city?: string | null;
      country?: string | null;
    };
    createdBy: {
      firstName: string;
      lastName: string;
      email: string;
    };
    items: InvoiceItem[];
    booking?: {
      bookingReference: string;
      travelStartDate?: string;
      travelEndDate?: string;
      totalAdults?: number;
      totalChildren?: number;
      totalInfants?: number;
      package?: {
        name: string;
      } | null;
    } | null;
  };
  logoUrl?: string;
}

function formatCurrency(amount: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const day = date.getDate();
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
}

function getInvoiceTitle(invoiceType: string): string {
  switch (invoiceType) {
    case 'CREDIT_NOTE':
      return 'CREDIT NOTE';
    case 'PROFORMA':
      return 'PROFORMA';
    case 'INVOICE':
    default:
      return 'INVOICE';
  }
}

function getStatusColors(status: string): { backgroundColor: string; color: string } {
  switch (status) {
    case 'PAID':
      return statusStyles.paid;
    case 'OVERDUE':
      return statusStyles.overdue;
    case 'SENT':
    case 'VIEWED':
      return statusStyles.sent;
    case 'PARTIALLY_PAID':
      return statusStyles.partiallyPaid;
    default:
      return statusStyles.draft;
  }
}

function getTravelersString(booking: InvoicePDFProps['invoice']['booking']): string {
  if (!booking) return '';

  const parts: string[] = [];

  if (booking.totalAdults && booking.totalAdults > 0) {
    parts.push(`${booking.totalAdults} Adult${booking.totalAdults > 1 ? 's' : ''}`);
  }
  if (booking.totalChildren && booking.totalChildren > 0) {
    parts.push(`${booking.totalChildren} Child${booking.totalChildren > 1 ? 'ren' : ''}`);
  }
  if (booking.totalInfants && booking.totalInfants > 0) {
    parts.push(`${booking.totalInfants} Infant${booking.totalInfants > 1 ? 's' : ''}`);
  }

  return parts.join(', ');
}

export function InvoicePDF({ invoice, logoUrl }: InvoicePDFProps) {
  // Bill-to details with fallbacks
  const billToName = invoice.billToName || `${invoice.customer.firstName} ${invoice.customer.lastName}`;
  const billToCompany = invoice.billToCompany;
  const billToTaxId = invoice.billToTaxId;
  const billToEmail = invoice.billToEmail || invoice.customer.email;
  const billToPhone = invoice.billToPhone || invoice.customer.phone;
  const billToAddress = invoice.billToAddress || [
    invoice.customer.address,
    invoice.customer.city,
    invoice.customer.country,
  ].filter(Boolean).join(', ');

  // Travelers string
  const travelersString = getTravelersString(invoice.booking);

  // Travel dates string
  const travelDatesString = invoice.booking?.travelStartDate && invoice.booking?.travelEndDate
    ? `${formatDate(invoice.booking.travelStartDate)} - ${formatDate(invoice.booking.travelEndDate)}`
    : invoice.booking?.travelStartDate
    ? formatDate(invoice.booking.travelStartDate)
    : '';

  // Get status colors
  const statusColor = getStatusColors(invoice.status);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            {logoUrl && (
              <Image src={logoUrl} style={styles.logo} />
            )}
            <View style={styles.companyDetails}>
              <Text style={styles.companyName}>{COMPANY.name}</Text>
              <Text style={styles.companyAddress}>{COMPANY.address}</Text>
              <Text style={styles.companyAddress}>{COMPANY.city}</Text>
              <Text style={styles.companyAddress}>{COMPANY.phone}</Text>
              <Text style={styles.companyAddress}>{COMPANY.email}</Text>
            </View>
          </View>
          <View style={styles.headerRight}>
            <Text style={styles.invoiceTitle}>{getInvoiceTitle(invoice.invoiceType)}</Text>
            <Text style={styles.invoiceNumber}>{invoice.invoiceNumber}</Text>
            <Text style={styles.invoiceDate}>Issue Date: {formatDate(invoice.issueDate)}</Text>
            <Text style={styles.invoiceDate}>Due Date: {formatDate(invoice.dueDate)}</Text>
            <View style={[styles.statusBadge, { backgroundColor: statusColor.backgroundColor }]}>
              <Text style={[styles.statusText, { color: statusColor.color }]}>
                {invoice.status.replace('_', ' ')}
              </Text>
            </View>
          </View>
        </View>

        {/* Bill To & Booking Reference Boxes */}
        <View style={styles.infoBoxesContainer}>
          {/* Bill To Box */}
          <View style={styles.infoBox}>
            <Text style={styles.infoBoxTitle}>Bill To</Text>
            <Text style={styles.infoBoxName}>{billToName}</Text>
            {billToCompany && (
              <Text style={styles.infoBoxText}>{billToCompany}</Text>
            )}
            {billToTaxId && (
              <Text style={styles.infoBoxText}>Tax ID: {billToTaxId}</Text>
            )}
            {billToAddress && (
              <Text style={styles.infoBoxText}>{billToAddress}</Text>
            )}
            {billToEmail && (
              <Text style={styles.infoBoxText}>{billToEmail}</Text>
            )}
            {billToPhone && (
              <Text style={styles.infoBoxText}>{billToPhone}</Text>
            )}
          </View>

          {/* Booking Reference Box */}
          <View style={styles.infoBox}>
            <Text style={styles.infoBoxTitle}>Booking Reference</Text>
            {invoice.booking ? (
              <>
                <Text style={styles.infoBoxName}>{invoice.booking.bookingReference}</Text>
                {invoice.booking.package && (
                  <>
                    <Text style={styles.infoBoxLabel}>Package</Text>
                    <Text style={styles.infoBoxValue}>{invoice.booking.package.name}</Text>
                  </>
                )}
                {travelDatesString && (
                  <>
                    <Text style={styles.infoBoxLabel}>Travel Dates</Text>
                    <Text style={styles.infoBoxValue}>{travelDatesString}</Text>
                  </>
                )}
                {travelersString && (
                  <>
                    <Text style={styles.infoBoxLabel}>Travelers</Text>
                    <Text style={styles.infoBoxValue}>{travelersString}</Text>
                  </>
                )}
              </>
            ) : (
              <Text style={styles.infoBoxText}>No booking associated</Text>
            )}
          </View>
        </View>

        {/* Line Items Table */}
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableHeaderCell, styles.tableCellDescription]}>Description</Text>
            <Text style={[styles.tableHeaderCell, styles.tableCellQty]}>Qty</Text>
            <Text style={[styles.tableHeaderCell, styles.tableCellPrice]}>Unit Price</Text>
            <Text style={[styles.tableHeaderCell, styles.tableCellTax]}>Tax</Text>
            <Text style={[styles.tableHeaderCell, styles.tableCellTotal]}>Total</Text>
          </View>
          {invoice.items.map((item, index) => {
            const isLast = index === invoice.items.length - 1;
            const isAlt = index % 2 === 1;

            // Select appropriate row style based on position and alternation
            const getRowStyle = () => {
              if (isLast) {
                return isAlt ? styles.tableRowAltNoBorder : styles.tableRowNoBorder;
              }
              return isAlt ? styles.tableRowAlt : styles.tableRow;
            };

            return (
              <View key={item.id} style={getRowStyle()}>
                <Text style={[styles.tableCell, styles.tableCellDescription]}>{item.description}</Text>
                <Text style={[styles.tableCell, styles.tableCellQty]}>{item.quantity}</Text>
                <Text style={[styles.tableCell, styles.tableCellPrice]}>
                  {formatCurrency(item.unitPrice, invoice.currency)}
                </Text>
                <Text style={[styles.tableCell, styles.tableCellTax]}>
                  {item.taxRate > 0 ? `${item.taxRate}%` : '-'}
                </Text>
                <Text style={[styles.tableCell, styles.tableCellTotal]}>
                  {formatCurrency(item.totalAmount, invoice.currency)}
                </Text>
              </View>
            );
          })}
        </View>

        {/* Totals Section */}
        <View style={styles.totalsContainer}>
          <View style={styles.totalsBox}>
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Subtotal</Text>
              <Text style={styles.totalValue}>
                {formatCurrency(invoice.subtotal, invoice.currency)}
              </Text>
            </View>
            {invoice.discountAmount > 0 && (
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>Discount</Text>
                <Text style={styles.discountValue}>
                  -{formatCurrency(invoice.discountAmount, invoice.currency)}
                </Text>
              </View>
            )}
            {invoice.taxAmount > 0 && (
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>VAT/KDV</Text>
                <Text style={styles.totalValue}>
                  {formatCurrency(invoice.taxAmount, invoice.currency)}
                </Text>
              </View>
            )}
            <View style={styles.grandTotalRow}>
              <Text style={styles.grandTotalLabel}>GRAND TOTAL</Text>
              <Text style={styles.grandTotalValue}>
                {formatCurrency(invoice.totalAmount, invoice.currency)}
              </Text>
            </View>
            {invoice.amountPaid > 0 && invoice.balanceDue > 0 && (
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>Amount Paid</Text>
                <Text style={styles.totalValue}>
                  {formatCurrency(invoice.amountPaid, invoice.currency)}
                </Text>
              </View>
            )}
            {invoice.balanceDue > 0 ? (
              <View style={styles.balanceDueRow}>
                <Text style={styles.balanceDueLabel}>BALANCE DUE</Text>
                <Text style={styles.balanceDueValue}>
                  {formatCurrency(invoice.balanceDue, invoice.currency)}
                </Text>
              </View>
            ) : (
              <View style={styles.paidInFullRow}>
                <Text style={styles.paidInFullLabel}>PAID IN FULL</Text>
                <Text style={styles.paidInFullValue}>
                  {invoice.paidAt ? formatDate(invoice.paidAt) : 'Thank you!'}
                </Text>
              </View>
            )}
          </View>
        </View>

        {/* Payment Details */}
        {invoice.balanceDue > 0 && (
          <View style={styles.paymentDetailsBox}>
            <Text style={styles.paymentDetailsTitle}>Payment Details</Text>
            <View style={styles.paymentDetailsRow}>
              <Text style={styles.paymentDetailsLabel}>Bank</Text>
              <Text style={styles.paymentDetailsValue}>{COMPANY.bank}</Text>
            </View>
            <View style={styles.paymentDetailsRow}>
              <Text style={styles.paymentDetailsLabel}>Account Name</Text>
              <Text style={styles.paymentDetailsValue}>{COMPANY.accountName}</Text>
            </View>
            <View style={styles.paymentDetailsRow}>
              <Text style={styles.paymentDetailsLabel}>IBAN</Text>
              <Text style={styles.paymentDetailsValue}>{COMPANY.iban}</Text>
            </View>
            <View style={styles.paymentDetailsRow}>
              <Text style={styles.paymentDetailsLabel}>Reference</Text>
              <Text style={styles.paymentDetailsValue}>{invoice.invoiceNumber}</Text>
            </View>
          </View>
        )}

        {/* Notes */}
        {invoice.notes && (
          <View style={styles.notesBox}>
            <Text style={styles.notesTitle}>Notes</Text>
            <Text style={styles.notesText}>{invoice.notes}</Text>
          </View>
        )}

        {/* Terms */}
        {invoice.terms && (
          <View style={styles.notesBox}>
            <Text style={styles.notesTitle}>Terms & Conditions</Text>
            <Text style={styles.notesText}>{invoice.terms}</Text>
          </View>
        )}

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerThankYou}>Thank you for choosing Polatkan Turizm!</Text>
          <Text style={styles.footerContact}>
            {COMPANY.phone} | {COMPANY.email}
          </Text>
        </View>
      </Page>
    </Document>
  );
}
